import { ErrorOverlay } from "@/components/overlay/error-overlay";
import {
  PublicAppStore,
  useAppStore,
  useInitializeAppStore,
  useInitializeGuestAppStore,
  usePublicAppStore,
} from "@/store";
import { QuickSearchModal } from "@/store/pages/SearchPageStore/QuickSearchModal/QuickSearchModal";
import { AppStoreContext, GuestAppStoreContext } from "@/store/utils/context";
import { observer } from "mobx-react-lite";
import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { localStorageModule } from "@/modules/local-storage";
import { Outlet } from "react-router";
import { LoadingOverlay } from "@/components/overlay";
import { appRoutes } from "@/app/router/appRoutes";
import { FloatingAgentLoadingOverlay } from "@/pages/floating-agent/floating-agent-window-chrome";
import { SyncStoreInitializationChecker } from "@/app/overlays/SyncStoreInitializationChecker";
import { BootstrapOverlay } from "@/app/overlays/BootstrapOverlay";
import { LogoutOverlay } from "@/app/overlays/LogoutOverlay";
import { UpgradedUserEmailChecker } from "@/app/overlays/UpgradedUserEmailChecker";
import { clientEnvModule } from "@/modules/client-env";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { UpsellModal } from "@/components/modal/upsell";
import {
  AppleProactiveUpgradeModal,
  ProactiveUpsellModal,
} from "@/components/modal/proactive-upsell";
import { WelcomeToProModal } from "@/components/modal/welcome-to-pro/welcome-to-pro";
import {
  clearImportSessionInProgress,
  readImportSessionsInProgress,
} from "@/modules/cache/import-session";
import { trackConversionWithRewardful } from "@/modules/rewardful";
import { ThemeType, useTheme } from "@/mds";
import { removeQueryParams } from "@/store/navigation/helpers";
import { subscriptionModule } from "@/modules/subscriptions";
import { StripeCheckoutSource } from "@/modules/subscriptions/query-params";
import { getAndClearPendingActions } from "@/app/router/handle-ephemeral-routes";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { OneClickImportModal } from "@/components/one-click-import/one-click-import-modal";
import { MemAgentSlackConnectedModal } from "@/components/modal/mem-agent-slack-connected";
import { IMessageReturnToMessagesModal } from "@/components/modal/imessage-return-to-messages/imessage-return-to-messages-modal";
import { SubscriptionPlanKind } from "@/store/subscriptions/types";
import {
  clearIMessageClaimCheckoutReturnToMessagesPending,
  consumeIMessageClaimCheckoutReturnToMessagesPending,
  markIMessageClaimCompletionSurfaceExited,
} from "@/modules/mem-agent/imessage-claim-flow-session";
import {
  getIMessageReturnToMessagesTitle,
  getIMessageReturnToMessagesUrl,
  type IMessageReturnToMessagesTitle,
} from "@/modules/mem-agent/imessage-return-to-messages";
import {
  confirmProactiveSubscriptionReturn,
  handleProactiveSubscriptionCancelReturn,
} from "./proactive-subscription-return";
import { handlePendingMemAgentSlackConnectedModal } from "./mem-agent-slack-connected-return";
import { openMemAgentSlackConnectedDestinationInNewTab } from "./mem-agent-slack-connected-action";
import { ProactiveCheckoutCompletionModal } from "./proactive-checkout-completion-modal";
import { windowModule } from "@/modules/window";
import { handleNonProactiveCheckoutReturn } from "./non-proactive-checkout-return";

interface OutletLoadingHandlers {
  setIsReady: () => void;
  setIsError: () => void;
  setLoadingMessage: (message: string) => void;
}

interface StoreProviderOutletProps {}

export const StoreProviderOutlet: FC<StoreProviderOutletProps> = observer(() => {
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);
  const [showErrorOverlay, setShowErrorOverlay] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const location = useLocation();

  const setIsReady = () => setShowLoadingOverlay(false);
  const setIsError = () => setShowErrorOverlay(true);

  // The floating-agent widget lives in a transparent BrowserWindow — the
  // standard full-screen loading overlay would paint an opaque backdrop.
  const isFloatingAgentWindow = location.pathname === appRoutes.floatingAgent({}).path;

  return (
    <>
      <ErrorOverlay isVisible={showErrorOverlay} />
      {isFloatingAgentWindow ? (
        <FloatingAgentLoadingOverlay isVisible={showLoadingOverlay} />
      ) : (
        <LoadingOverlay isVisible={showLoadingOverlay} loadingMessage={loadingMessage} />
      )}
      <StoreProviderOutletContent
        setIsReady={setIsReady}
        setIsError={setIsError}
        setLoadingMessage={setLoadingMessage}
      />
    </>
  );
});

export const StoreProviderOutletContent: FC<OutletLoadingHandlers> = observer(
  ({ setIsReady, setIsError, setLoadingMessage }) => {
    const { publicStore } = usePublicAppStore();

    if (publicStore.auth.isGuestMode) {
      return (
        <GuestModeStoreProviderOutlet
          publicStore={publicStore}
          setIsReady={setIsReady}
          setIsError={setIsError}
          setLoadingMessage={setLoadingMessage}
        />
      );
    }

    if (publicStore.auth.isStandardMode) {
      return (
        <StandardModeStoreProviderOutlet
          publicStore={publicStore}
          setIsReady={setIsReady}
          setIsError={setIsError}
          setLoadingMessage={setLoadingMessage}
        />
      );
    }

    /**
     * If the user is not in guest mode or standard mode, we
     * don't wrap the outlet in any store providers.
     */
    return <Outlet />;
  }
);

const GuestModeStoreProviderOutlet: FC<{ publicStore: PublicAppStore } & OutletLoadingHandlers> =
  observer(({ publicStore, setIsReady, setIsError }) => {
    const state = useInitializeGuestAppStore({
      publicAppStore: publicStore,
    });

    useEffect(() => {
      if (state.loading) {
        return;
      }

      if (state.error || !state.data) {
        setIsError();

        return;
      }

      setIsReady();
    }, [setIsError, setIsReady, state.data, state.error, state.loading]);

    if (!state.data) {
      return null;
    }

    return (
      <GuestAppStoreContext.Provider value={state.data}>
        <Outlet />
      </GuestAppStoreContext.Provider>
    );
  });

const StandardModeStoreProviderOutlet: FC<{ publicStore: PublicAppStore } & OutletLoadingHandlers> =
  observer(({ publicStore, setIsReady, setIsError, setLoadingMessage }) => {
    const state = useInitializeAppStore({
      publicAppStore: publicStore,
    });

    useEffect(() => {
      if (state.loading) {
        return;
      }

      if (state.error || !state.data) {
        setIsError();

        return;
      }

      /**
       * Note that we don't call `setIsReady` here because we also need
       * the StandardModeStoreProviderOutletContent to be ready.
       */
    }, [setIsError, setIsReady, state.data, state.error, state.loading]);

    if (!state.data) {
      return null;
    }

    return (
      <AppStoreContext.Provider value={state.data}>
        <StandardModeStoreProviderOutletContent
          setIsReady={setIsReady}
          setIsError={setIsError}
          setLoadingMessage={setLoadingMessage}
        />
      </AppStoreContext.Provider>
    );
  });

/**
 * We use this to kick-off global side effects in a hook.
 */
const StandardModeStoreProviderOutletContent: FC<OutletLoadingHandlers> = observer(
  ({ setIsReady, setIsError, setLoadingMessage }) => {
    const { store, pageStore } = useAppStore();
    const { themeType, setTheme } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const processedStripeSearchRef = useRef<string | null>(null);
    const [iMessageReturnToMessagesTitle, setIMessageReturnToMessagesTitle] =
      useState<IMessageReturnToMessagesTitle | null>(null);
    const isSyncInitializationFailed = store.sync.initializationFailed;
    const upgradePlanFlowIsSupported =
      Boolean(store.readyState.data) && !clientEnvModule.isDesktop();

    store.useInitializeAppStoreEffects();
    pageStore.useInitializePageStoreEffects();

    const finishUpgradePlanFlow = useCallback(
      async ({ paramsToRemove = [] }: { paramsToRemove?: string[] } = {}) => {
        const upgradePlanParams = subscriptionModule.parseUpgradePlanQueryParams(location);
        const callbackUrl = store.subscriptions.upgradePlanFlowCallbackUrl;

        try {
          if (callbackUrl) {
            await windowModule.navigateToExternal({ url: new URL(callbackUrl) });
          }
        } finally {
          store.subscriptions.finishUpgradePlanFlow();
          navigate(
            removeQueryParams({
              location,
              paramsToRemove: [...upgradePlanParams.paramsToRemove, ...paramsToRemove],
            }),
            { replace: true }
          );
        }
      },
      [location, navigate, store.subscriptions]
    );

    const closeProactiveUpgradeModal = useCallback(() => {
      if (store.subscriptions.upgradePlanFlowIsActive) {
        if (store.subscriptions.upgradePlanFlowCheckoutReturnIsConfirming) {
          return;
        }

        void finishUpgradePlanFlow();
        return;
      }

      store.subscriptions.closeProactiveUpsell();
    }, [finishUpgradePlanFlow, store.subscriptions]);

    const closeAppleProactiveUpgradeModal = useCallback(() => {
      if (store.subscriptions.upgradePlanFlowIsActive) {
        if (store.subscriptions.upgradePlanFlowCheckoutReturnIsConfirming) {
          return;
        }

        void finishUpgradePlanFlow();
        return;
      }

      store.subscriptions.closeAppleProactiveUpgradeModal();
    }, [finishUpgradePlanFlow, store.subscriptions]);

    const showIMessageReturnToMessagesModal = useCallback(() => {
      markIMessageClaimCompletionSurfaceExited();
      setIMessageReturnToMessagesTitle(getIMessageReturnToMessagesTitle({ scenario: "proactive" }));
    }, []);

    const maybeShowIMessageCheckoutReturnToMessagesModal = useCallback(
      ({ confirmed }: { confirmed: boolean }) => {
        if (!consumeIMessageClaimCheckoutReturnToMessagesPending()) {
          return;
        }

        if (!confirmed) {
          return;
        }

        showIMessageReturnToMessagesModal();
      },
      [showIMessageReturnToMessagesModal]
    );

    const closeIMessageReturnToMessagesModal = useCallback(() => {
      setIMessageReturnToMessagesTitle(null);
    }, []);

    const redirectIMessageReturnToMessagesModal = useCallback(() => {
      setIMessageReturnToMessagesTitle(null);
      void windowModule.navigateToExternalAndPreserveHistory({
        url: getIMessageReturnToMessagesUrl(),
      });
    }, []);

    // Handle pending ephemeral route actions (processed before router initialized)
    useEffectOnMount(() => {
      const processPendingActions = async () => {
        const pendingActions = await getAndClearPendingActions();
        for (const { config, data } of pendingActions) {
          config.handler(data, { store, pageStore });
        }
      };
      processPendingActions();
    });

    useEffect(() => {
      if (isSyncInitializationFailed) {
        // Keep only the sync initialization failure modal visible.
        setIsReady();
        return;
      }

      if (store.readyState.error) {
        setIsError();

        logger.error({
          message: "[StandardModeStoreProviderOutletContent] Error.",
          info: { error: objectModule.safeErrorAsJson(store.readyState.error) },
        });

        return;
      }

      if (store.readyState.loading) {
        return;
      }

      if (!store.readyState.data) {
        setIsError();

        logger.error({
          message: "[StandardModeStoreProviderOutletContent] Missing data.",
        });

        return;
      }

      setIsReady();
    }, [
      isSyncInitializationFailed,
      setIsError,
      setIsReady,
      store.readyState.data,
      store.readyState.error,
      store.readyState.loading,
    ]);

    // Coerce dark theme to light for free users after store is ready
    useEffect(() => {
      if (!store.readyState.data) return;
      const isAllowedDark = store.subscriptions.featureAccess?.isPaid ?? false;

      if (themeType === ThemeType.HarvestMoon && !isAllowedDark) {
        setTheme(ThemeType.Harvest);
      }
    }, [setTheme, store.readyState.data, store.subscriptions.featureAccess?.isPaid, themeType]);

    useEffect(() => {
      if (!store.readyState.data) return;

      void handlePendingMemAgentSlackConnectedModal({
        pathname: location.pathname,
        store,
      });
    }, [location.pathname, store, store.readyState.data]);

    // Reconcile any import sessions left in-progress after a hard refresh
    useEffect(() => {
      if (!store.readyState.data) return;

      const cleanupInProgressImportSessions = async () => {
        const markers = await readImportSessionsInProgress();
        const sessionIds = new Set(markers.map(m => m.sessionId));

        for (const m of markers) {
          try {
            await store.sessions.updateSessionOnNewUserMessage(m.sessionId, { status: "ERROR" });
          } catch {
            // ignore per-session failure
          } finally {
            await clearImportSessionInProgress(m.sessionId);
          }
        }

        for (const sessionId of sessionIds) {
          store.sessionEvents.createMessageSentSessionEvent({
            session_id: sessionId,
            message_value: {
              kind: "REASONING",
              content: "Uploads were interrupted",
            },
          });

          trackEvent(TrackedEvent.ImportFilesBatchFailed, {
            session_id: sessionId,
            context: "StandardModeStoreProviderOutletContent",
          });
        }
      };

      cleanupInProgressImportSessions();
    }, [store.readyState.data, store.sessionEvents, store.sessions]);

    const bootstrapProgressMessage = store.sync.bootstrapProgressMessage;
    useEffect(() => {
      setLoadingMessage(bootstrapProgressMessage);
    }, [bootstrapProgressMessage, setLoadingMessage]);

    useLayoutEffect(() => {
      if (!upgradePlanFlowIsSupported) {
        return;
      }

      const upgradePlanParams = subscriptionModule.parseUpgradePlanQueryParams(location);

      if (!upgradePlanParams.isProactiveUpgradePlanFlow) {
        return;
      }

      store.subscriptions.startUpgradePlanFlow({
        callbackUrl: upgradePlanParams.callbackUrl,
      });
    }, [location, store.subscriptions, upgradePlanFlowIsSupported]);

    // Handle Stripe success/cancel params globally and strip them immediately
    useLayoutEffect(() => {
      const stripeParams = subscriptionModule.parseStripeQueryParams(location);
      const upgradePlanParams = subscriptionModule.parseUpgradePlanQueryParams(location);
      const checkoutStartedFromUpgradePlanFlow =
        upgradePlanFlowIsSupported && upgradePlanParams.isProactiveUpgradePlanFlow;
      const isStripeReturn = stripeParams.isCheckoutSuccess || stripeParams.isBillingReturn;

      if (isStripeReturn && processedStripeSearchRef.current === location.search) {
        return;
      }

      if (stripeParams.isCheckoutSuccess) {
        processedStripeSearchRef.current = location.search;

        if (stripeParams.isProactiveCheckout) {
          if (checkoutStartedFromUpgradePlanFlow) {
            store.subscriptions.startUpgradePlanFlowCheckoutReturnConfirmation();
          }

          void confirmProactiveSubscriptionReturn({
            store,
            checkoutOnboardingStep: stripeParams.checkoutOnboardingStep,
            checkoutStartedFromOnboarding:
              stripeParams.checkoutSource === StripeCheckoutSource.Onboarding,
            checkoutStartedFromUpgradePlanFlow,
            stripeSessionId: stripeParams.sessionId,
            originalPlanKind: stripeParams.originalPlanKind,
            originalEffectivePlanKind: stripeParams.originalEffectivePlanKind,
          }).then(confirmed => {
            maybeShowIMessageCheckoutReturnToMessagesModal({ confirmed });

            if (confirmed) {
              const email = store.account.myAccount.profileEmailAddress;
              if (email) trackConversionWithRewardful({ email });
            }

            if (!checkoutStartedFromUpgradePlanFlow) {
              return;
            }

            if (confirmed) {
              void finishUpgradePlanFlow({ paramsToRemove: stripeParams.paramsToRemove });
              return;
            }

            store.subscriptions.finishUpgradePlanFlowCheckoutReturnConfirmation();
          });
        } else {
          void handleNonProactiveCheckoutReturn({
            isCommercialCheckout: stripeParams.isCommercialCheckout,
            store,
            stripeSessionId: stripeParams.sessionId ?? undefined,
            commercialCatalogGeneration: stripeParams.commercialCatalogGeneration,
            targetCommercialPlanKey: stripeParams.targetCommercialPlanKey,
          }).then(confirmed => {
            if (!confirmed) {
              return;
            }

            const email = store.account.myAccount.profileEmailAddress;
            if (email) trackConversionWithRewardful({ email });
            if (stripeParams.sessionId) {
              trackEvent(TrackedEvent.PaywallUpgradeReturn, {
                stripe_session_id: stripeParams.sessionId,
                ...(stripeParams.commercialCatalogGeneration
                  ? {
                      commercial_catalog_generation: stripeParams.commercialCatalogGeneration,
                    }
                  : {}),
                ...(stripeParams.targetCommercialPlanKey
                  ? {
                      commercial_plan_key: stripeParams.targetCommercialPlanKey,
                      target_commercial_plan_key: stripeParams.targetCommercialPlanKey,
                      commercial_upgrade_action: "select_plan",
                    }
                  : {}),
              });
            }
          });
        }

        if (stripeParams.sessionId) {
          localStorageModule.writeStripeLastCheckoutSession(stripeParams.sessionId);
        }

        navigate(removeQueryParams({ location, paramsToRemove: stripeParams.paramsToRemove }), {
          replace: true,
        });
      } else if (stripeParams.isCheckoutCancel) {
        trackEvent(TrackedEvent.PaywallCancelReturn, {
          ...(stripeParams.commercialCatalogGeneration
            ? { commercial_catalog_generation: stripeParams.commercialCatalogGeneration }
            : {}),
          ...(stripeParams.targetCommercialPlanKey
            ? {
                commercial_plan_key: stripeParams.targetCommercialPlanKey,
                target_commercial_plan_key: stripeParams.targetCommercialPlanKey,
                commercial_upgrade_action: "select_plan",
              }
            : {}),
        });
        if (stripeParams.isProactiveCheckout) {
          clearIMessageClaimCheckoutReturnToMessagesPending();
          handleProactiveSubscriptionCancelReturn({
            store,
            checkoutOnboardingStep: stripeParams.checkoutOnboardingStep,
            checkoutStartedFromOnboarding:
              stripeParams.checkoutSource === StripeCheckoutSource.Onboarding,
            checkoutStartedFromUpgradePlanFlow,
          });
        }
        navigate(removeQueryParams({ location, paramsToRemove: stripeParams.paramsToRemove }), {
          replace: true,
        });
      } else if (stripeParams.isBillingReturn && stripeParams.hasMemPro) {
        processedStripeSearchRef.current = location.search;

        // We redirected them to billing portal because they were already paid;
        if (stripeParams.isProactiveCheckout) {
          if (checkoutStartedFromUpgradePlanFlow) {
            store.subscriptions.startUpgradePlanFlowCheckoutReturnConfirmation();
          }

          void confirmProactiveSubscriptionReturn({
            store,
            checkoutOnboardingStep: stripeParams.checkoutOnboardingStep,
            checkoutStartedFromOnboarding:
              stripeParams.checkoutSource === StripeCheckoutSource.Onboarding,
            checkoutStartedFromUpgradePlanFlow,
            stripeSessionId: stripeParams.sessionId,
          }).then(confirmed => {
            maybeShowIMessageCheckoutReturnToMessagesModal({ confirmed });

            if (!checkoutStartedFromUpgradePlanFlow) {
              return;
            }

            if (confirmed) {
              void finishUpgradePlanFlow({ paramsToRemove: stripeParams.paramsToRemove });
              return;
            }

            store.subscriptions.finishUpgradePlanFlowCheckoutReturnConfirmation();
          });
        } else {
          // optimistically show Pro now that they're back
          store.platformInfo.setSubscriptionCompletedSuccessfully(SubscriptionPlanKind.PRO);
          // Refresh the platform info
          void store.platformInfo.fetchPlatformInfo();
        }

        navigate(removeQueryParams({ location, paramsToRemove: stripeParams.paramsToRemove }), {
          replace: true,
        });
      } else if (stripeParams.isBillingReturn) {
        processedStripeSearchRef.current = location.search;
        clearIMessageClaimCheckoutReturnToMessagesPending();

        // Coming back from billing portal (manually opened, not auto-redirected)
        // Just refresh without optimistically showing Pro
        store.platformInfo.fetchPlatformInfo();

        navigate(removeQueryParams({ location, paramsToRemove: stripeParams.paramsToRemove }), {
          replace: true,
        });
      }
    }, [
      location,
      navigate,
      store,
      store.platformInfo,
      store.account.myAccount.profileEmailAddress,
      upgradePlanFlowIsSupported,
      finishUpgradePlanFlow,
      maybeShowIMessageCheckoutReturnToMessagesModal,
    ]);

    const handleMemAgentSlackConnectedClick = useCallback(() => {
      const slackUrl = store.memAgent.slackAppUrl ?? store.memAgent.slackWorkspaceUrl;

      trackEvent(TrackedEvent.MemAgentSlackOpenClick, {
        source: "connected_modal",
        slack_workspace_id: store.memAgent.slackWorkspaceId,
        slack_team_name: store.memAgent.slackTeamName,
      });

      void openMemAgentSlackConnectedDestinationInNewTab({
        closeSlackConnectedModal: store.memAgent.closeSlackConnectedModal,
        slackUrl,
      });
    }, [store.memAgent]);

    if (isSyncInitializationFailed) {
      return <SyncStoreInitializationChecker />;
    }

    if (!store.readyState.data) {
      return (
        <>
          <BootstrapOverlay />
        </>
      );
    }

    return (
      <>
        <SyncStoreInitializationChecker />
        <BootstrapOverlay />
        <LogoutOverlay />
        <UpgradedUserEmailChecker />
        <QuickSearchModal />
        <OneClickImportModal />
        {store.subscriptions.upsellModalIsOpen ? (
          <UpsellModal
            isOpen={store.subscriptions.upsellModalIsOpen}
            onClose={store.subscriptions.closeUpsell}
            gatedFeature={store.subscriptions.upsellModalFeature}
            level={store.subscriptions.upsellModalLevel}
          />
        ) : null}
        <ProactiveUpsellModal
          isOpen={store.subscriptions.proactiveUpsellModalIsOpen}
          onClose={closeProactiveUpgradeModal}
          checkoutResetKey={store.subscriptions.proactiveCheckoutResetKey}
          checkoutIsExternallyPending={
            store.subscriptions.upgradePlanFlowCheckoutReturnIsConfirming
          }
        />
        <AppleProactiveUpgradeModal
          isOpen={store.subscriptions.appleProactiveUpgradeModalIsOpen}
          onClose={closeAppleProactiveUpgradeModal}
          checkoutResetKey={store.subscriptions.proactiveCheckoutResetKey}
          checkoutIsExternallyPending={
            store.subscriptions.upgradePlanFlowCheckoutReturnIsConfirming
          }
        />
        <ProactiveCheckoutCompletionModal />
        <WelcomeToProModal
          isOpen={store.platformInfo.welcomeToProModalIsOpen}
          onClose={store.platformInfo.closeWelcomeToProModal}
        />
        <MemAgentSlackConnectedModal
          isOpen={store.memAgent.isSlackConnectedModalOpen}
          trackingContext={store.memAgent.slackConnectedModalViewTrackingContext}
          onClick={handleMemAgentSlackConnectedClick}
          onClose={store.memAgent.closeSlackConnectedModal}
        />
        <IMessageReturnToMessagesModal
          isOpen={iMessageReturnToMessagesTitle !== null}
          title={iMessageReturnToMessagesTitle ?? "Welcome to Mem Proactive"}
          onClose={closeIMessageReturnToMessagesModal}
          onRedirect={redirectIMessageReturnToMessagesModal}
        />
        <Outlet />
      </>
    );
  }
);
